import { useCallback } from 'react';
import { useLookupCustomerByCaptchaLazyQuery } from '../types/front.graphql';
import { useReCaptcha } from '@kidslabs/module-core';
import { ErrorCodesEnum } from '../enums';
import type { ResultData } from '../types';

export interface LookupCustomerResult extends ResultData {
    isExistedPhone?: boolean;
}

/**
 * useLookupCustomer — Checks if a phone number is already registered.
 *
 * Uses the `lookupCustomerByCaptcha` query with Google reCAPTCHA token
 * for server-side validation (separate from the HMAC-signed `lookupCustomer`
 * query used by the mobile app).
 *
 * Usage:
 * - In register flow: call before requesting OTP to determine
 *   if the user should be redirected to login instead.
 */
export const useLookupCustomer = () => {
    const [fetchLookupCustomerByCaptcha] = useLookupCustomerByCaptchaLazyQuery();
    const { retrieveCaptchaToken } = useReCaptcha();

    return useCallback(
        async (phoneNumber: string): Promise<LookupCustomerResult> => {
            try {
                const captchaToken = await retrieveCaptchaToken('lookupCustomer', error => {
                    throw new Error(`Captcha verification failed: ${error}`);
                });

                const result = await fetchLookupCustomerByCaptcha({
                    variables: {
                        phone_number: phoneNumber,
                        captchaResponseToken: captchaToken,
                    },
                    fetchPolicy: 'network-only',
                });

                if (result.error) {
                    return {
                        message: result.error.message || 'Failed to lookup customer',
                        errorCode: ErrorCodesEnum.INVALID_REQUEST,
                        isExistedPhone: undefined,
                    };
                }

                const isExistedPhone = result.data?.lookupCustomerByCaptcha?.is_existed_phone ?? false;

                return {
                    message: isExistedPhone ? 'Phone number already registered' : 'Phone number is available',
                    errorCode: ErrorCodesEnum.SUCCESS,
                    isExistedPhone,
                };
            } catch (error) {
                const errorMessage = error instanceof Error ? error.message : 'Failed to lookup customer';
                return {
                    message: errorMessage,
                    errorCode: ErrorCodesEnum.INVALID_REQUEST,
                    isExistedPhone: undefined,
                };
            }
        },
        [fetchLookupCustomerByCaptcha, retrieveCaptchaToken]
    );
};
